import { Injectable } from '@angular/core';
import { DomainGroupRepoService } from '@client/app/services/api/domain-groups-repo/domain-group-repo.service';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { Domain } from '@monsido/ng2/modules/models/api/domain';
import { DomainGroupModel } from '@client/app/services/api/domain-groups-repo/domain-group-model';

export interface Rule {
    match: string;
    match_rule?: string;
    key?: string;
    text?: string;
    label_ids?: string[];
    exclude: boolean;
}

interface DomainGroupForValidation {
    title?: string;
    rules?: Array<{
        match?: string;
        match_rule?: string;
        text?: string;
        key?: string;
        label_ids?: string[];
    }>;
}

interface DomainGroupWithRules {
    rules: Rule[];
}

@Injectable({
    providedIn: 'root',
})
export class DomainGroupService {

    constructor (
        private domainGroupRepoService: DomainGroupRepoService,
        private domainRepoService: DomainRepoService,
    ) {}

    create (domain: Domain, params: any, headers?: any): Promise<DomainGroupModel> {
        return this.domainGroupRepoService.create(domain, params);
    }

    update (domain: Domain, group: DomainGroupModel, headers?: any): Promise<DomainGroupModel> {
        return this.domainGroupRepoService.update(domain, group);
    }

    destroy (domainId: number, id: number): Promise<void> {
        return this.domainGroupRepoService.destroy(domainId, id);
    }

    async updateDomainGroupRules (domainId: number): Promise<Domain> {
        return this.domainRepoService.updateDomainGroupRules(domainId);
    }

    async updateSingleDomainGroupRules (domainId: number, groupId: number): Promise<unknown> {
        return this.domainRepoService.updateSingleDomainGroupRules(domainId, groupId);
    }

    cleanupRules (rules: Rule[]): Rule[] {
        if (Array.isArray(rules)) {
            return rules
                .filter((rule: Rule) => {
                    if (rule.match === 'labels') {
                        return rule.label_ids && rule.label_ids.length !== 0;
                    }
                    return !!(rule.text && rule.match_rule && rule.match && rule.exclude !== undefined);
                })
                .map((rule: Rule) => {
                    return this.cleanupRuleProperties(rule);
                });
        }

        return rules;
    }

    cleanupRuleProperties (rule: Rule): Rule {
        if (rule.match === 'labels') {
            const out: Rule = {
                match: rule.match,
                exclude: rule.exclude,
            };
            if (rule.label_ids !== undefined) {
                out.label_ids = rule.label_ids;
            }
            return out;
        } else {
            return {
                match: rule.match,
                match_rule: rule.match_rule ?? '',
                exclude: rule.exclude,
                key: rule.key ?? '',
                text: rule.text ?? '',
            };
        }
    }

    getDomainGroupLabels (domainGroup: DomainGroupWithRules, labelsMap: { [key: string]: any }): any[] {
        return domainGroup.rules.map((rule: Rule) => {
            return rule.label_ids?.map(id => {
                return {
                    ...labelsMap[id],
                    excluded: rule.exclude,
                };
            }) || [];
        }).flat();
    }

    isDomainGroupComplete (domainGroup: DomainGroupForValidation): boolean {
        if (!domainGroup?.title || domainGroup.title.trim() === '') {
            return false;
        }

        if (!domainGroup.rules || domainGroup.rules.length === 0) {
            return false;
        }

        return domainGroup.rules.every((rule) => {
            if (!rule.match) {
                return false;
            }

            if (rule.match === 'labels') {
                return !!(rule.label_ids && rule.label_ids.length > 0);
            }

            if (!rule.match_rule || !rule.text || rule.text.trim() === '') {
                return false;
            }

            if (rule.match === 'metadata' && (!rule.key || rule.key.trim() === '')) {
                return false;
            }

            return true;
        });
    }
}
